import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TippyDirective } from '@ngneat/helipopper';
import { Subscription, debounceTime, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import findKey from 'lodash-es/findKey';
import { AddressAutocompleteModule } from '@ee/common/address-autocomplete';
import { STATES } from '@ee/common/constants';
import { CourthouseSelectorComponent } from '@ee/common/courthouse-selector';
import { CourtChangeRequestDialogComponent, CourtChangeRequestDialogData } from '@ee/common/court-change-request';
import { CaseAddressFillOptions, RolesEnum, SubdivisionSource } from '@ee/common/enums';
import {
  AddressSuggestion,
  County,
  CountySubdivisions,
  Court,
  IntakeOption,
  SubdivisionKind,
  SubdivisionLookupResult
} from '@ee/common/models';
import { CountyService } from '@ee/common/services';
import { COMPOSITE_PARTS, CompositePart } from './composites';
import { IntakeHostContext } from './intake-host-context';

/** v1's "how the court got chosen" badge: a township match, the county default, or by hand. */
type CourtAutoSelectionSource = 'subdivision' | 'default' | null;

/** v1's precinct hint states, in the order the template switches on them. */
type SubdivisionHint = 'manual' | 'matched' | 'none' | 'ambiguous' | 'no-coordinates' | null;

/**
 * The property address, as one question — and the venue that follows from it.
 *
 * The reason it is a composite and not seven fields is behavioural, not visual: answering the street
 * line fills four of the others, and the county follows from the state and ZIP. A form that asked for
 * all seven separately would be asking a client to type what it already knows.
 *
 * Everything from the county down is a port of v1's property step
 * (`eviction-intake/forms/eviction-property-section.component.ts`), rule for rule, because the inbox and
 * the server read what that step submits: the county *record* (fee settings, courts, processing), the
 * courts an attorney chose, the coordinates, and the precinct with its provenance. The first v2 pass
 * submitted the county as a name string under a key nothing reads, so every submission arrived with no
 * county at all. Where v1 and this differ it is a bug; the parity specs beside this file say which rule
 * each case pins.
 *
 * Split out of the renderer rather than added to it. The renderer draws placements and is used by the
 * builder's preview, where a live geocoder and a county lookup are exactly what must not run — a canvas
 * that redraws as you drag would issue a search per frame. So the renderer keeps the inert drawing and
 * this owns the live behaviour.
 */
interface ParkedVenue {
  countyId: string;
  subdivision: unknown;
  source: unknown;
  kind: unknown;
  place: unknown;
}

/** See `AddressBlockComponent.memory`. */
interface VenueMemory {
  /** The first mount has recorded what the form opened with. */
  recorded: boolean;
  initialCounty?: County;
  lastCountyId?: string;
  subdivisionValueCountyId?: string;
  placeCountyId?: string;
  parkedVenue: ParkedVenue | null;
  courtPickedByUser: boolean;
}

const VENUE_MEMORY = new WeakMap<FormGroup, VenueMemory>();

/** The venue the block resolves rather than asks: never in the renderer's `parts`, always drawn when the form has them. */
const CARRIED_PART_KEYS = new Set(COMPOSITE_PARTS.ADDRESS.filter(part => part.carried).map(part => part.key));

/** The parts a hand edit to which means a different place than the autocomplete located. */
const ADDRESS_PART_KEYS = ['property_street_address', 'property_city', 'property_state', 'property_zip_code'];

@Component({
  selector: 'ee-address-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    TippyDirective,
    AddressAutocompleteModule,
    CourthouseSelectorComponent
  ],
  template: `
    <div class="address">
      <div class="address__grid">
        <div class="address__street">
          <ee-address-autocomplete [formControl]="control('property_street_address')"
                                   [placeholder]="labelOf('property_street_address')"
                                   [required]="isRequired('property_street_address')"
                                   [selectedState]="value('property_state')"
                                   [selectedCity]="value('property_city')"
                                   [compact]="compact"
                                   (selectionChange)="onSuggestion($event)"></ee-address-autocomplete>
        </div>

        @if (has('property_unit_type')) {
          <mat-form-field appearance="outline" class="address__unit-type" [class.compact]="compact">
            <mat-label>{{ labelOf('property_unit_type') }}</mat-label>
            <mat-select [formControl]="control('property_unit_type')">
              @for (type of unitTypes; track type) {
                <mat-option [value]="type">{{ type }}</mat-option>
              }
            </mat-select>
            <mat-hint>{{ hintFor('property_unit_type') }}</mat-hint>
          </mat-form-field>
        }

        @if (has('property_unit_number')) {
          <mat-form-field appearance="outline" class="address__unit-number" [class.compact]="compact">
            <mat-label>{{ labelOf('property_unit_number') }}</mat-label>
            <input matInput [formControl]="control('property_unit_number')" />
            <mat-hint>{{ hintFor('property_unit_number') }}</mat-hint>
          </mat-form-field>
        }

        <!-- City, state, ZIP: the order an address is written in, and the order v1 asked for it. -->
        <mat-form-field appearance="outline" class="address__city" [class.compact]="compact">
          <mat-label>{{ labelOf('property_city') }}</mat-label>
          <input matInput [formControl]="control('property_city')" />
          <mat-hint>{{ hintFor('property_city') }}</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="address__state" [class.compact]="compact">
          <mat-label>{{ labelOf('property_state') }}</mat-label>
          <mat-select [formControl]="control('property_state')">
            @for (state of states; track state) {
              <mat-option [value]="state">{{ state }}</mat-option>
            }
          </mat-select>
          <mat-hint>{{ hintFor('property_state') }}</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="address__zip" [class.compact]="compact">
          <mat-label>{{ labelOf('property_zip_code') }}</mat-label>
          <input matInput inputmode="numeric" maxlength="5" [formControl]="control('property_zip_code')" />
          <mat-hint>{{ hintFor('property_zip_code') }}</mat-hint>
        </mat-form-field>

        @if (showCounty()) {
          <!--
            The county record, not its name: fee settings, the court list and processing all key off
            county.id. ZIP matches sort first; a client rule, then the coordinates, then the ZIP pick
            the default, and the attorney sees why (v1's hints, verbatim).
          -->
          <mat-form-field appearance="outline" class="address__county" [class.compact]="compact">
            <mat-label>{{ labelOf('property_county') }}</mat-label>
            <mat-select [formControl]="control('county')" [compareWith]="sameCounty"
                        [disabled]="countyClientOverridden() && !attorney()" (selectionChange)="onCountyPicked()">
              <mat-select-trigger>{{ selectedCounty?.name }}</mat-select-trigger>
              <mat-option [value]="null">-- None --</mat-option>
              @for (county of counties(); track county.id) {
                <mat-option [value]="county">
                  {{ county.name }}@if (county.county_name_override && county.county_name_override !== county.name) { ({{ county.county_name_override }})}
                </mat-option>
              }
            </mat-select>
            @if (control('county').hasError('required')) {
              <mat-error>Please select a county.</mat-error>
            }
            <mat-hint>
              {{ optionRequired('property_county') ? 'Required.' : 'Optional.' }}
              @if (countyClientOverridden()) {
                <span class="blue cursor-pointer"
                      tp="The county selection has been overridden by your client's county rules.">
                  <i class="fa fa-info-circle blue"></i>
                  Overridden via rule.
                </span>
              }
              @if (countyMatches() > 1 && !countyClientOverridden() && attorney()) {
                @if (!geometryResolved()) {
                  <span class="red cursor-pointer"
                        tp="This zip code spans more than one county.
                      Consider setting up a client county rule to automatically override the county
                      for this zip code.">
                    <i class="fa fa-info-circle red"></i>
                    Multiple matches.
                  </span>
                } @else {
                  <span class="orange cursor-pointer"
                        tp="This zip code spans more than one county. The county was matched using the property coordinates.">
                    <i class="fa fa-info-circle orange"></i>
                    Matched via coordinates.
                  </span>
                }
              }
            </mat-hint>
          </mat-form-field>
        }

        @if (selectedCounty && subdivisions()?.enabled && has('containing_subdivision') && showSubdivision()) {
          <mat-form-field appearance="outline" class="address__subdivision" [class.compact]="compact">
            <mat-label>{{ subdivisionLabel }}</mat-label>
            <mat-select [formControl]="control('containing_subdivision')" (selectionChange)="onSubdivisionChangedByUser()">
              @for (subdivision of subdivisionOptions; track subdivision.value) {
                <mat-option [value]="subdivision.value">{{ subdivision.label }}</mat-option>
              }
            </mat-select>
            @if (control('containing_subdivision').hasError('required')) {
              <mat-error>Please select a {{ subdivisionLabelLower }}.</mat-error>
            }
            <mat-hint>
              {{ optionRequired('property_subdivision') ? 'Required.' : 'Optional.' }}
              @switch (subdivisionHint()) {
                @case ('manual') {
                  <span class="blue cursor-pointer"
                        [tp]="'This ' + subdivisionLabelLower + ' was selected by hand, so it is never changed automatically.'">
                    <i class="fa fa-info-circle blue"></i>
                    Entered manually.
                  </span>
                }
                @case ('matched') {
                  <span class="orange cursor-pointer"
                        [tp]="'The ' + subdivisionLabelLower + ' was matched using the property coordinates.'">
                    <i class="fa fa-info-circle orange"></i>
                    Matched via coordinates.
                  </span>
                }
                @case ('none') {
                  <span class="red">
                    <i class="fa fa-info-circle red"></i>
                    Could not determine the {{ subdivisionLabelLower }} from the property address. Select it manually.
                  </span>
                }
                @case ('ambiguous') {
                  <span class="red">
                    <i class="fa fa-info-circle red"></i>
                    The property address falls on a {{ subdivisionLabelLower }} boundary. Select the
                    {{ subdivisionLabelLower }} manually.
                  </span>
                }
                @case ('no-coordinates') {
                  <span>
                    Enter the address with autocomplete to detect the {{ subdivisionLabelLower }}, or select it
                    manually.
                  </span>
                }
              }
            </mat-hint>
          </mat-form-field>
        }

        @if (selectedCounty && subdivisionKind === 'PRECINCT' && has('place') && showPlace()) {
          <mat-form-field appearance="outline" class="address__place" [class.compact]="compact">
            <mat-label>Place</mat-label>
            <input matInput [formControl]="control('place')" />
            @if (control('place').hasError('required')) {
              <mat-error>Please enter a place.</mat-error>
            }
            <mat-hint>{{ optionRequired('property_place') ? 'Required.' : 'Optional.' }}</mat-hint>
          </mat-form-field>
        }

        @if (courts()?.length && showCourts()) {
          <ee-courthouse-selector class="address__courts" [courts]="courts()!" [formControl]="control('courts')"
                                  label="Court(s)" hint="Optional." (selectionChange)="onCourtPicked()">
            @if (courtAutoSelectionSource() === 'subdivision') {
              <span class="blue cursor-pointer"
                    tp="The court was automatically selected because the property address falls within a township/subdivision served by this court.">
                <i class="fa fa-info-circle blue"></i> Matched via township.
              </span>
            } @else if (courtAutoSelectionSource() === 'default') {
              <span class="orange cursor-pointer"
                    tp="No court matched the property's township. The default court for this county was selected.">
                <i class="fa fa-info-circle orange"></i> Default court (no township match).
              </span>
            }
          </ee-courthouse-selector>
        }
      </div>

      @if (canRequestCourtChange && showCourts()) {
        <div class="address__court-change">
          <button mat-button type="button" class="request-court-change-button"
                  [disabled]="courtsLoading() || courtsLoadFailed()"
                  tp="Request that our team add a missing court or correct an existing one. Your request is submitted for review — it does not change the court immediately."
                  (click)="openCourtChangeRequest()">
            Can't find your court? Request a change
          </button>
        </div>
      }
    </div>
  `,
  styleUrl: './address-block.component.scss'
})
export class AddressBlockComponent implements OnInit, OnDestroy {
  @Input({ required: true }) form!: FormGroup;

  @Input() parts: CompositePart[] = [];

  @Input() unitTypes: string[] = [];

  /** The compact density (MODERN) or v1's full-height fields (CLASSIC). The host decides; compact by default. */
  @Input() compact = true;

  private readonly countyService = inject(CountyService);
  private readonly dialog = inject(MatDialog);
  protected readonly host = inject(IntakeHostContext);

  private readonly subs: Subscription[] = [];
  private countyLookupSub: Subscription | null = null;
  /** Which address the county lookup in flight is for; a geometry answer for an older one is dropped. */
  private countyLookupToken = 0;
  private courtsFetchSub: Subscription | null = null;
  private recommendCourtSub: Subscription | null = null;
  private subdivisionsSub: Subscription | null = null;
  private subdivisionLookupSub: Subscription | null = null;

  protected readonly states = Object.keys(STATES);

  // County.
  protected readonly counties = signal<County[]>([]);
  protected readonly countyMatches = signal(0);
  protected readonly countyClientOverridden = signal(false);
  protected readonly geometryResolved = signal(false);
  /**
   * What this form's venue remembers across mounts.
   *
   * The wizard destroys the block when it leaves the Property page and builds a new one on return,
   * while the form lives on. Held on the instance, "the county the form opened with" became whichever
   * county was selected when the page was last entered, and a court the filer had picked by hand was
   * recommended over again. Keyed by the form, the facts survive the component.
   */
  private get memory(): VenueMemory {
    let memory = VENUE_MEMORY.get(this.form);
    if (!memory) {
      memory = { recorded: false, parkedVenue: null, courtPickedByUser: false };
      VENUE_MEMORY.set(this.form, memory);
    }
    return memory;
  }

  /** The county the form opened with (a clone): its court and precinct are kept, not re-chosen. */
  private get initialCounty(): County | undefined {
    return this.memory.initialCounty;
  }
  private set initialCounty(county: County | undefined) {
    this.memory.initialCounty = county;
  }

  // Courts.
  protected readonly courts = signal<Court[] | undefined>(undefined);
  private courtsCountyId: string | undefined;
  protected readonly courtsLoadFailed = signal(false);
  protected readonly courtsLoading = signal(false);
  protected readonly courtAutoSelectionSource = signal<CourtAutoSelectionSource>(null);

  // Precinct / township.
  protected readonly subdivisions = signal<CountySubdivisions | null>(null);
  private subdivisionsCountyId: string | undefined;
  private get subdivisionValueCountyId(): string | undefined {
    return this.memory.subdivisionValueCountyId;
  }
  private set subdivisionValueCountyId(id: string | undefined) {
    this.memory.subdivisionValueCountyId = id;
  }
  /** The county the place was typed for; a place does not outlive its county. */
  private get placeCountyId(): string | undefined {
    return this.memory.placeCountyId;
  }
  private set placeCountyId(id: string | undefined) {
    this.memory.placeCountyId = id;
  }

  /**
   * The township and place as they were when the county was cleared, and whose county they were for.
   *
   * A lookup clears the county while it runs (so a stale one is never submitted) and that clears what
   * the county owned. When the same county comes back — the usual case: a street edit, a suggestion
   * for the same address — v1 kept a hand-picked township, and so does this; another county drops it.
   */
  private get parkedVenue(): ParkedVenue | null {
    return this.memory.parkedVenue;
  }
  private set parkedVenue(parked: ParkedVenue | null) {
    this.memory.parkedVenue = parked;
  }
  protected readonly subdivisionLookupState = signal<Exclude<SubdivisionHint, 'manual' | 'matched'>>(null);

  ngOnInit(): void {
    // An explicit client-facing host submits with no court. A prefilled or cloned value must not ride along.
    if (this.host.allowCourtSelection() === false) {
      this.form.get('courts')?.setValue([], { emitEvent: false });
    }

    /*
     * Coordinates belong to the address the autocomplete chose. A hand edit to any part of it — the ZIP
     * corrected, the street retyped — is a different place, and the old point would otherwise choose
     * the county, the precinct and the court for an address no longer on the form. Registered before the
     * lookups below, so a state edit runs its lookup without the stale point.
     */
    ADDRESS_PART_KEYS.forEach(key => {
      const part = this.form.get(key);
      if (part) {
        this.subs.push(part.valueChanges.subscribe(() => this.clearCoordinates()));
      }
    });

    const state = this.form.get('property_state');
    if (state) {
      this.subs.push(state.valueChanges.subscribe(() => this.stateChanged()));
    }

    /*
     * Five digits, debounced, and only when it changed: a lookup per keystroke is five requests for one
     * ZIP, and the first four answer a prefix. The autocomplete sets the ZIP with `emitEvent: false` for
     * the same reason — otherwise selecting a suggestion fires this as well and the two race.
     */
    const zip = this.form.get('property_zip_code');
    if (zip) {
      this.subs.push(
        zip.valueChanges
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            filter((value: string) => (value ?? '').length === 5)
          )
          .subscribe(() => this.stateChanged())
      );
    }

    const county = this.form.get('county');
    if (county) {
      const current = (county.value ?? undefined) as County | undefined;
      if (!this.memory.recorded) {
        // The first mount for this form: what it opened with is the clone's, kept rather than re-chosen.
        // A later mount finds whatever this intake has done since, which is not "initial".
        this.memory.recorded = true;
        this.initialCounty = current;
        this.memory.lastCountyId = current?.id;
        this.placeCountyId = current?.id;
        if (this.form.get('containing_subdivision')?.value) {
          this.subdivisionValueCountyId = current?.id;
        }
      }
      if (current) {
        // Every mount: the county's court list and precinct configuration are drawn again for what is
        // selected, without choosing anything — the form's own selection is kept.
        if (this.courtSelectionEnabled) {
          this.initializeCourtsFromCounty(current);
        }
        this.onCountySelectedForSubdivision(current);
      }
      this.subs.push(county.valueChanges.subscribe((selected: County | null | undefined) => this.onCountyChanged(selected ?? undefined)));
    }

    // Place narrows a multi-candidate court match, so a typed place has to re-ask for the recommendation.
    const place = this.form.get('place');
    if (place) {
      // A recommendation in flight is for the place as it was: cancelled on the keystroke, not after
      // the debounce, or it could choose a court for the old place in the meantime and stand.
      this.subs.push(
        place.valueChanges.subscribe(() => {
          this.recommendCourtSub?.unsubscribe();
          this.recommendCourtSub = null;
        })
      );
      this.subs.push(
        place.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.tryRecommendCourt(this.selectedCounty))
      );
    }

    this.prefillFromClient();
    this.syncRequired();
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.countyLookupToken++;
    this.countyLookupSub?.unsubscribe();
    this.courtsFetchSub?.unsubscribe();
    this.recommendCourtSub?.unsubscribe();
    this.subdivisionsSub?.unsubscribe();
    this.subdivisionLookupSub?.unsubscribe();
  }

  // ---------------------------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------------------------

  /**
   * Whether this render has a home for a part.
   *
   * An *asked* part is drawn only when the host placed it (`parts` is what the renderer passes, which
   * is `askedParts` — the typed address). A *carried* part — the county record, the precinct, the
   * place, the courts — is never in that list by design, and is drawn whenever the form has its
   * control: gating it on `parts` as well left the whole venue step dark in the real wizard while the
   * specs, which handed the block every part, saw it.
   */
  protected has(key: string): boolean {
    if (!this.form.get(key)) {
      return false;
    }
    return CARRIED_PART_KEYS.has(key) || this.parts.some(part => part.key === key);
  }

  protected labelOf(key: string): string {
    return this.parts.find(part => part.key === key)?.label ?? key;
  }

  protected control(key: string): FormControl {
    return (this.form.get(key) as FormControl) ?? new FormControl(null);
  }

  protected value(key: string): string {
    return (this.form.get(key)?.value ?? '') as string;
  }

  /** What every field on the intake says under itself, in `ee-field`'s words. */
  protected hintFor(key: string): string {
    return this.isRequired(key) ? 'Required.' : 'Optional.';
  }

  /** `Validators.required` is a stable reference, so the control can simply be asked. */
  protected isRequired(key: string): boolean {
    return this.form.get(key)?.hasValidator(Validators.required) === true;
  }

  protected get selectedCounty(): County | undefined {
    return (this.form.get('county')?.value as County | null | undefined) ?? undefined;
  }

  /** A re-fetched list holds new objects for the same counties; the select must still find the stored one. */
  protected sameCounty(a: County | null, b: County | null): boolean {
    return (a?.id ?? a) === (b?.id ?? b);
  }

  private option(key: string): IntakeOption | undefined {
    return this.host.options()?.[key];
  }

  protected optionRequired(key: string): boolean {
    return this.option(key)?.required === true;
  }

  protected attorney(): boolean {
    return this.host.attorneyFilling();
  }

  /**
   * v1's visibility rule for the venue controls, verbatim: an attorney, or no logged-in org (the public
   * link), or the option's `show`. The county's default is the option itself; the precinct defaults to
   * shown and the place to hidden, as v1's builder seeds them.
   */
  protected showCounty(): boolean {
    return this.attorney() || this.host.publicLink() || !!this.option('property_county')?.show;
  }

  protected showSubdivision(): boolean {
    return (
      this.host.jurisdiction() && (this.attorney() || this.host.publicLink() || (this.option('property_subdivision')?.show ?? true))
    );
  }

  protected showPlace(): boolean {
    return (
      this.host.jurisdiction() && (this.attorney() || this.host.publicLink() || (this.option('property_place')?.show ?? false))
    );
  }

  /** Court is chosen by an attorney filling the form, or later in the inbox; client-facing renders never. */
  protected get courtSelectionEnabled(): boolean {
    if (!this.host.jurisdiction()) {
      return false;
    }
    const allowed = this.host.allowCourtSelection();
    return allowed !== null ? allowed : this.attorney();
  }

  protected showCourts(): boolean {
    return this.courtSelectionEnabled && (this.option('property_court')?.show ?? true);
  }

  /** The same gate the court-change endpoint applies: an attorney who is an admin or account admin. */
  protected get canRequestCourtChange(): boolean {
    const authorities: unknown[] = this.host.loggedInUser()?.authorities ?? [];
    return (
      authorities.includes(RolesEnum.ATTORNEY) &&
      (authorities.includes(RolesEnum.ADMIN) || authorities.includes(RolesEnum.ACCOUNT_ADMIN))
    );
  }

  protected get subdivisionLabel(): string {
    return this.subdivisions()?.label ?? 'Subdivision';
  }

  protected get subdivisionLabelLower(): string {
    return this.subdivisionLabel.toLowerCase();
  }

  protected get subdivisionKind(): SubdivisionKind | null {
    return this.subdivisions()?.kind ?? null;
  }

  protected get subdivisionOptions(): { value: string; label: string }[] {
    const options = (this.subdivisions()?.options ?? []).map(name => ({ value: name, label: name }));
    const stored = this.form.get('containing_subdivision')?.value as string | null;
    if (stored && !options.some(option => option.value === stored)) {
      options.push({ value: stored, label: `${stored} (not a listed ${this.subdivisionLabelLower})` });
    }
    return options;
  }

  protected subdivisionHint(): SubdivisionHint {
    const source = this.form.get('containing_subdivision_source')?.value;
    if (source === SubdivisionSource.MANUAL) {
      return 'manual';
    }
    if (source === SubdivisionSource.GEOLOCATION) {
      return 'matched';
    }
    if (!source && this.form.get('containing_subdivision')?.value) {
      return 'matched';
    }
    return this.subdivisionLookupState();
  }

  /**
   * v1 required these through the template's `[required]`, so a control is required exactly while its
   * field is on screen and its option says so. Re-applied whenever what is on screen changes.
   */
  private syncRequired(): void {
    this.applyRequired('county', this.showCounty() && this.optionRequired('property_county'));
    this.applyRequired(
      'containing_subdivision',
      !!this.selectedCounty && !!this.subdivisions()?.enabled && this.showSubdivision() && this.optionRequired('property_subdivision')
    );
    this.applyRequired(
      'place',
      !!this.selectedCounty && this.subdivisionKind === 'PRECINCT' && this.showPlace() && this.optionRequired('property_place')
    );
  }

  private applyRequired(key: string, required: boolean): void {
    const control = this.form.get(key);
    if (!control || control.hasValidator(Validators.required) === required) {
      return;
    }
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  // ---------------------------------------------------------------------------------------------
  // Client prefill (v1 ngOnInit)
  // ---------------------------------------------------------------------------------------------

  private prefillFromClient(): void {
    const client = this.host.selectedClient();
    const settings = client?.client_settings;
    const street = this.form.get('property_street_address');
    if (settings?.address_fill_option === `${CaseAddressFillOptions.FILL_WITH_CLIENT_ADDRESS}` && street && !street.value) {
      this.form.patchValue({
        property_street_address: client?.street_address_1,
        property_city: client?.city,
        property_state: client?.state,
        property_zip_code: client?.zip_code
      });
      // The state's valueChanges runs the county lookup.
    } else if (!this.host.publicLink() && this.form.get('property_state')?.value) {
      // A state already set (a clone): list its counties without re-choosing the one it carries.
      this.stateChanged(false);
    }

    // Prefills, like the address above: the block is built again each time the wizard returns to its
    // page, and an unconditional write here would put the client's default back over what was typed.
    const name = this.form.get('property_name');
    if (client?.property_name && name && !name.value) {
      name.setValue(client.property_name);
    }

    const state = this.form.get('property_state');
    if (
      settings?.address_fill_option === `${CaseAddressFillOptions.AUTO_COMPLETE_ADDRESS}` &&
      settings.preselect_address_state &&
      state &&
      !state.value
    ) {
      state.setValue(settings.preselect_address_state);
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Address and county (v1 onAddressSelected / stateChanged / processCounties)
  // ---------------------------------------------------------------------------------------------

  protected onSuggestion(suggestion: AddressSuggestion): void {
    if (!suggestion) {
      return;
    }
    const state = findKey(STATES, name => name === suggestion.state_full) ?? suggestion.state;
    const currentState = this.form.get('property_state')?.value;

    // The one moment the coordinates are known: they pick the county among ZIP matches and locate the
    // precinct. Every part is written silently — a hand edit clears the point, and this is not one.
    this.form.get('property_lat')?.setValue(suggestion.lat, { emitEvent: false });
    this.form.get('property_lng')?.setValue(suggestion.lng, { emitEvent: false });
    this.form.get('property_street_address')?.setValue(suggestion.street_address, { emitEvent: false });
    this.form.get('property_city')?.setValue(suggestion.city, { emitEvent: false });
    this.form.get('property_zip_code')?.setValue(suggestion.zip_code, { emitEvent: false });
    if (state !== currentState) {
      this.form.get('property_state')?.setValue(state, { emitEvent: false });
    }
    this.stateChanged();
  }

  /**
   * Forget the autocomplete's point, and choose the county again without it.
   *
   * A geometry answer still in flight is about that point; re-running the lookup moves the token, so
   * that answer is dropped and the ZIP decides. Only when there was a point to forget — a lookup per
   * keystroke on a hand-typed address is what the ZIP debounce exists to avoid. A state or ZIP edit
   * runs its own lookup as well, which simply supersedes this one.
   */
  /**
   * The filer chose a county from the list. A geometry answer still out for the address would otherwise
   * land on top of that choice; the lookup token moves on, so it is dropped.
   */
  protected onCountyPicked(): void {
    this.countyLookupToken++;
    this.geometryResolved.set(false);
  }

  /**
   * The filer chose a court. A recommendation still out must not replace it, no badge claims it, and
   * none asked later for this county (a township typed, the page revisited) replaces it either.
   */
  protected onCourtPicked(): void {
    this.recommendCourtSub?.unsubscribe();
    this.recommendCourtSub = null;
    this.courtAutoSelectionSource.set(null);
    this.memory.courtPickedByUser = true;
  }

  private clearCoordinates(): void {
    const had = this.form.get('property_lat')?.value != null || this.form.get('property_lng')?.value != null;
    this.form.get('property_lat')?.setValue(null, { emitEvent: false });
    this.form.get('property_lng')?.setValue(null, { emitEvent: false });
    if (had) {
      // A township found *from* the point goes with it: kept, it would be restored for the same county
      // with no point left to re-check it against, and stand for the edited address. A hand-picked one
      // is the filer's and stays.
      if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.GEOLOCATION) {
        this.clearSubdivision();
      }
      this.stateChanged();
    }
  }

  private stateChanged(updateSelectedCounty = true): void {
    const state = this.form.get('property_state')?.value as string | null;
    const zip = this.form.get('property_zip_code')?.value as string | null;
    this.countyLookupSub?.unsubscribe();
    const token = ++this.countyLookupToken;
    if (!state) {
      // No state, no venue: the old state's county cannot stand, and an answer still out for it must
      // not put it back. Clearing the county clears its courts, precinct and place with it.
      this.counties.set([]);
      this.countyMatches.set(0);
      this.countyClientOverridden.set(false);
      this.geometryResolved.set(false);
      if (updateSelectedCounty) {
        this.form.patchValue({ county: null });
        this.initialCounty = undefined;
      }
      return;
    }
    // The previous state's counties are not this address's, pending or not: offered while the new
    // list is in flight, one could be chosen and would stand if the request failed.
    this.counties.set([]);
    this.countyMatches.set(0);
    this.countyClientOverridden.set(false);
    this.geometryResolved.set(false);
    if (updateSelectedCounty) {
      // The previous state's county is wrong the moment the state changes, not once the list arrives —
      // a form left valid on it while the request is pending (or failed) would submit it.
      this.form.patchValue({ county: null });
      this.initialCounty = undefined;
    }
    this.countyLookupSub = this.countyService.getCountiesByState(state).subscribe({
      next: counties => void this.processCounties(state, counties ?? [], zip, updateSelectedCounty, token),
      // Already cleared on start; a failure leaves nothing to choose from, and a superseded one is ignored.
      error: () => undefined
    });
  }

  private async processCounties(
    state: string,
    counties: County[],
    zip: string | null,
    updateSelectedCounty: boolean,
    token: number
  ): Promise<void> {
    this.countyMatches.set(0);
    this.countyClientOverridden.set(false);
    this.geometryResolved.set(false);

    if (updateSelectedCounty) {
      this.form.patchValue({ county: null });
      this.initialCounty = undefined;
    }

    const zipInt = zip ? parseInt(zip, 10) : 0;
    const sorted: County[] = [];
    const matching: County[] = [];
    counties.forEach(county => {
      county.matches_zip_code = zipInt > 0 ? (county.zip_codes ?? []).indexOf(zipInt) > -1 : false;
      if (county.matches_zip_code) {
        sorted.unshift(county);
        matching.push(county);
      } else {
        sorted.push(county);
      }
    });
    this.counties.set(sorted);
    this.countyMatches.set(matching.length);

    // 1. The client's county rule for this state + ZIP wins outright.
    let overrideApplied = false;
    const rules = this.host.selectedClient()?.county_override_rules;
    if (updateSelectedCounty && rules?.length && state && zipInt > 0) {
      const rule = rules.find(r => r.state === state && r.zip_code === zipInt);
      const match = rule && sorted.find(c => c.id === rule.county_id);
      if (match) {
        this.form.patchValue({ county: match });
        this.countyClientOverridden.set(true);
        overrideApplied = true;
      }
    }

    // 2. The coordinates, when there are any. 3. Otherwise the ZIP.
    if (updateSelectedCounty && !overrideApplied) {
      const lat = this.form.get('property_lat')?.value;
      const lng = this.form.get('property_lng')?.value;
      if (lat != null && lng != null) {
        try {
          const geoCounty = await firstValueFrom(this.countyService.getGeometryMatch(lat, lng, state));
          // Unsubscribing the state list cannot reach this await: a slower answer about the previous
          // address must not patch its county over the one chosen since.
          if (token !== this.countyLookupToken) {
            return;
          }
          if (geoCounty) {
            const match = sorted.find(c => c.id === geoCounty.id);
            if (match) {
              this.form.patchValue({ county: match });
            } else {
              geoCounty.matches_zip_code = false;
              this.counties.set([geoCounty, ...sorted]);
              this.form.patchValue({ county: geoCounty });
            }
            this.geometryResolved.set(true);
          } else {
            this.selectFirstZipMatch(matching);
          }
        } catch {
          if (token !== this.countyLookupToken) {
            return;
          }
          this.selectFirstZipMatch(matching);
        }
      } else {
        this.selectFirstZipMatch(matching);
      }
    }

    if (!updateSelectedCounty) {
      // A refresh of the list the county is chosen from, not a choice: the selected county is swapped for
      // its listed copy silently, so nothing downstream re-chooses the court or precinct it already has.
      const current = this.form.get('county')?.value as County | null;
      const listed = sorted.find(c => c.id === current?.id) ?? null;
      if (current && !listed) {
        // The saved county is not one of this state's: it goes, and so must everything that depended on
        // it — through the ordinary change, so the mirror, courts, township and place go with it.
        this.form.get('county')?.setValue(null);
      } else {
        this.form.get('county')?.setValue(listed, { emitEvent: false });
        this.form.get('property_county')?.setValue(listed?.name ?? null, { emitEvent: false });
      }
    }
  }

  /** A single ZIP match is the county. Among several, v1 prefers the one named "… County". */
  private selectFirstZipMatch(matching: County[]): void {
    if (!matching.length) {
      return;
    }
    const preferred = matching.length > 1 ? matching.find(c => c.name?.endsWith('County')) || matching[0] : matching[0];
    this.form.patchValue({ county: preferred });
  }

  // ---------------------------------------------------------------------------------------------
  // County changed → courts and precinct (v1 ngAfterViewInit's county subscription)
  // ---------------------------------------------------------------------------------------------

  private onCountyChanged(selected: County | undefined): void {
    // The name mirror: what the PM case path and the review summary read.
    this.form.get('property_county')?.setValue(selected?.name ?? null, { emitEvent: false });

    // The county the form opened with is "initial" only while it is still selected. Once the filer has
    // moved off it, coming back is a choice like any other — its court is chosen afresh, not the one
    // picked for the county in between.
    if (this.initialCounty && selected?.id !== this.initialCounty.id) {
      this.initialCounty = undefined;
    }
    // A court picked by hand was picked for a county; another county starts the recommendation afresh.
    if (selected && selected.id !== this.memory.lastCountyId) {
      this.memory.courtPickedByUser = false;
    }
    if (selected) {
      this.memory.lastCountyId = selected.id;
    }

    // A place names somewhere in the county it was typed for. Moving to another county — precincts or
    // not — cannot keep it, or it would ride into that county's court recommendation and onto the case.
    // Choosing the first county keeps whatever was typed; there was no other county for it to belong to.
    // Losing the county altogether is handled below, where the place is parked before it is cleared —
    // and the owner id has to still be there for the parking to know whose place it was.
    if (selected) {
      if (this.placeCountyId !== undefined && selected.id !== this.placeCountyId) {
        this.form.get('place')?.setValue(null, { emitEvent: false });
      }
      this.placeCountyId = selected.id;
    }

    // A recommendation or court list still in flight is about a county no longer selected.
    this.recommendCourtSub?.unsubscribe();
    this.recommendCourtSub = null;
    this.courtsFetchSub?.unsubscribe();
    this.courtsFetchSub = null;
    this.courtsLoading.set(false);

    if (!this.courtSelectionEnabled) {
      // Client-facing renders may select a county and must not select a court; the court list is an
      // authenticated call a public visitor cannot make.
      this.courtsLoadFailed.set(false);
      this.courts.set(undefined);
      this.courtsCountyId = undefined;
    } else if (selected && (selected.court_ids?.length ?? 0) > 0) {
      this.fetchCourtsForCounty(selected);
    } else {
      this.courtsLoadFailed.set(false);
      this.courts.set(selected?.courts ?? undefined);
      this.courtsCountyId = selected?.id;
      this.preselectCourt(selected);
    }

    this.onCountySelectedForSubdivision(selected);
    this.syncRequired();
  }

  private initializeCourtsFromCounty(county: County): void {
    if ((county.court_ids?.length ?? 0) > 0) {
      this.courtsFetchSub?.unsubscribe();
      this.courtsLoadFailed.set(false);
      this.courtsLoading.set(true);
      this.courtsFetchSub = this.countyService.getCountyWithCourts(county.id).subscribe({
        next: withCourts => {
          this.courts.set(withCourts.courts ?? undefined);
          this.courtsCountyId = county.id;
          this.courtsLoading.set(false);
          // No preselect: the form's own selection is kept.
        },
        error: () => {
          this.courts.set(county.courts ?? undefined);
          this.courtsCountyId = county.id;
          this.courtsLoadFailed.set(!this.courts()?.length);
          this.courtsLoading.set(false);
        }
      });
    } else {
      this.courtsLoadFailed.set(false);
      this.courts.set(county.courts ?? undefined);
      this.courtsCountyId = county.id;
    }
  }

  private fetchCourtsForCounty(county: County): void {
    this.courtsFetchSub?.unsubscribe();
    this.courtsFetchSub = null;
    this.courtsLoadFailed.set(false);
    // Drop the previous county's list — and its selected court — before asking: holding either would
    // offer the wrong county's courts, or submit one while this county's list is still loading. Not on a
    // refresh of the county the form opened with (the state list re-patching the same county while its
    // first court fetch is still out): that court is the saved one, and `preselectCourt` keeps it.
    if (this.courtsCountyId !== county.id) {
      this.courts.set(undefined);
      this.courtsCountyId = undefined;
      if (this.initialCounty?.id !== county.id) {
        this.form.get('courts')?.setValue([], { emitEvent: false });
      }
    }
    this.courtsLoading.set(true);
    this.courtsFetchSub = this.countyService.getCountyWithCourts(county.id).subscribe({
      next: withCourts => {
        this.courts.set(withCourts.courts ?? undefined);
        this.courtsCountyId = county.id;
        this.courtsLoading.set(false);
        this.preselectCourt(county);
        this.tryRecommendCourt(county);
      },
      error: () => {
        this.courts.set(county.courts ?? undefined);
        this.courtsCountyId = county.id;
        this.courtsLoadFailed.set(!this.courts()?.length);
        this.courtsLoading.set(false);
        this.preselectCourt(county);
        this.tryRecommendCourt(county);
      }
    });
  }

  /** For a new county: the court serving the city, else the first. The initial county keeps its court. */
  private preselectCourt(county: County | undefined): void {
    if (this.initialCounty && this.initialCounty.id === county?.id) {
      return;
    }
    if (county && this.memory.courtPickedByUser) {
      return;
    }
    const courts = this.courts();
    if (county) {
      if (courts?.length) {
        const city = this.form.get('property_city')?.value;
        const match = courts.find(c => c.cities?.includes(city));
        this.form.get('courts')?.setValue([match ?? courts[0]]);
      } else {
        this.form.get('courts')?.setValue([]);
      }
    } else {
      this.courts.set(undefined);
      this.form.get('courts')?.setValue([]);
    }
  }

  /**
   * Once this county's courts and precinct configuration have both settled, ask for the court that
   * serves the precinct (and, for a precinct county, the place). Either request may land last; the
   * guards make the earlier arrival a no-op, so whichever completes the pair recommends.
   */
  private tryRecommendCourt(county: County | undefined): void {
    if (!this.courtSelectionEnabled) return;
    const courts = this.courts();
    if (!county || !courts?.length) return;
    if (this.courtsCountyId !== county.id) return;
    if (this.subdivisionsCountyId !== county.id) return;
    if (this.initialCounty && this.initialCounty.id === county.id) return;
    if (this.memory.courtPickedByUser) return;

    this.courtAutoSelectionSource.set(null);

    const subdivision = this.form.get('containing_subdivision')?.value as string | null;
    if (!subdivision) return;

    const place =
      this.subdivisionKind === 'PRECINCT' ? (this.form.get('place')?.value as string | null)?.trim() || undefined : undefined;

    this.recommendCourtSub?.unsubscribe();
    this.recommendCourtSub = this.countyService.recommendCourt(county.id, subdivision, place).subscribe({
      next: recommended => {
        if (!recommended) return;
        const match = courts.find(c => c.id === recommended.id || c.name === recommended.name);
        if (match) {
          this.form.get('courts')?.setValue([match]);
          const bySubdivision = match.supported_subdivisions?.some(s => s.toLowerCase() === subdivision.toLowerCase());
          this.courtAutoSelectionSource.set(bySubdivision ? 'subdivision' : 'default');
        }
      },
      // The existing selection stands.
      error: () => undefined
    });
  }

  protected openCourtChangeRequest(): void {
    const data: CourtChangeRequestDialogData = {
      courts: this.courts() ?? [],
      selectedCourtId: (this.form.get('courts')?.value as Court[] | null)?.[0]?.id ?? undefined,
      state: this.form.get('property_state')?.value ?? undefined,
      countyOrVenue: this.selectedCounty?.name ?? undefined
    };
    this.dialog.open(CourtChangeRequestDialogComponent, { data, width: '640px', autoFocus: false });
  }

  // ---------------------------------------------------------------------------------------------
  // Precinct / township (v1's subdivision flow)
  // ---------------------------------------------------------------------------------------------

  protected onSubdivisionChangedByUser(): void {
    this.form.get('containing_subdivision_source')?.setValue(SubdivisionSource.MANUAL);
    this.form.get('containing_subdivision_kind')?.setValue(this.subdivisionKind);
    this.subdivisionValueCountyId = this.selectedCounty?.id;
    this.subdivisionLookupState.set(null);
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;
    this.tryRecommendCourt(this.selectedCounty);
  }

  private onCountySelectedForSubdivision(county: County | undefined): void {
    if (!county) {
      this.subdivisionsSub?.unsubscribe();
      this.subdivisionsSub = null;
      this.subdivisionLookupSub?.unsubscribe();
      this.subdivisionLookupSub = null;
      // No county, no precinct or place: the controls disappear with the county, and a value left
      // behind in them is still submitted — a precinct from the previous address on a filing. Parked
      // against the county they were for, in case it is the one that comes back.
      const subdivision = this.form.get('containing_subdivision')?.value;
      const owner = this.subdivisionValueCountyId ?? this.placeCountyId;
      if (owner && (subdivision || this.form.get('place')?.value)) {
        this.parkedVenue = {
          countyId: owner,
          subdivision,
          source: this.form.get('containing_subdivision_source')?.value,
          kind: this.form.get('containing_subdivision_kind')?.value,
          place: this.form.get('place')?.value
        };
      }
      if (subdivision) {
        this.clearSubdivision();
      }
      this.form.get('place')?.setValue(null, { emitEvent: false });
      this.placeCountyId = undefined;
      return;
    }
    const parked = this.parkedVenue;
    this.parkedVenue = null;
    if (parked?.countyId === county.id) {
      // The same county again: what it owned stands, as v1's township did across a refresh.
      this.form.get('containing_subdivision')?.setValue(parked.subdivision, { emitEvent: false });
      this.form.get('containing_subdivision_source')?.setValue(parked.source, { emitEvent: false });
      this.form.get('containing_subdivision_kind')?.setValue(parked.kind, { emitEvent: false });
      this.form.get('place')?.setValue(parked.place, { emitEvent: false });
      this.subdivisionValueCountyId = parked.subdivision ? county.id : undefined;
      this.placeCountyId = county.id;
    }
    if (!this.host.jurisdiction()) {
      return;
    }
    if (this.subdivisionsCountyId !== county.id || !this.subdivisions()) {
      this.fetchSubdivisions(county);
      return;
    }
    this.resolveSubdivision(county);
  }

  private fetchSubdivisions(county: County): void {
    this.subdivisionsSub?.unsubscribe();
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;
    this.subdivisions.set(null);
    this.subdivisionsCountyId = undefined;
    this.subdivisionLookupState.set(null);

    this.subdivisionsSub = this.countyService.getSubdivisions(county.id).subscribe({
      next: result => {
        this.subdivisions.set(result);
        this.subdivisionsCountyId = county.id;
        this.dropValueOfAnotherCounty(county);
        this.resolveSubdivision(county);
        this.syncRequired();
      },
      error: () => {
        this.subdivisionsCountyId = county.id;
        this.dropValueOfAnotherCounty(county);
        this.tryRecommendCourt(county);
        this.syncRequired();
      }
    });
  }

  /**
   * A township belongs to the county it was chosen in. Another county — whether or not it has townships
   * of its own — cannot keep it: a MANUAL pick would otherwise survive the change, be shown as "not
   * listed", and name the court and the case for a place in the wrong county.
   */
  private dropValueOfAnotherCounty(county: County): void {
    if (!this.form.get('containing_subdivision')?.value) return;
    const owner = this.subdivisionValueCountyId ?? this.initialCounty?.id;
    if (!owner || owner === county.id) return;
    this.clearSubdivision();
  }

  private clearSubdivision(): void {
    this.subdivisionValueCountyId = undefined;
    this.form.get('containing_subdivision')?.setValue(null);
    this.form.get('containing_subdivision_source')?.setValue(null);
    this.form.get('containing_subdivision_kind')?.setValue(null);
    this.subdivisionLookupState.set(null);
  }

  private resolveSubdivision(county: County): void {
    if (!this.startSubdivisionLookup(county)) {
      this.tryRecommendCourt(county);
    }
  }

  private startSubdivisionLookup(county: County): boolean {
    this.subdivisionLookupSub?.unsubscribe();
    this.subdivisionLookupSub = null;

    if (!this.subdivisions()?.enabled) {
      this.subdivisionLookupState.set(null);
      return false;
    }
    if (!this.form.get('containing_subdivision')) {
      return false;
    }
    if (!this.showSubdivision()) {
      this.subdivisionLookupState.set(null);
      return false;
    }
    if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL) {
      return false;
    }
    if (this.initialCounty?.id === county.id && this.form.get('containing_subdivision')?.value) {
      return false;
    }

    const state = this.form.get('property_state')?.value;
    const lat = this.form.get('property_lat')?.value;
    const lng = this.form.get('property_lng')?.value;
    if (lat == null || lng == null || !state) {
      this.subdivisionLookupState.set('no-coordinates');
      return false;
    }

    this.subdivisionLookupSub = this.countyService.lookupSubdivision(lat, lng, state).subscribe({
      next: (result: SubdivisionLookupResult) => {
        if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL) {
          return;
        }
        if (result?.subdivision) {
          this.form.get('containing_subdivision')?.setValue(result.subdivision);
          this.form.get('containing_subdivision_source')?.setValue(SubdivisionSource.GEOLOCATION);
          this.form.get('containing_subdivision_kind')?.setValue(result.kind ?? this.subdivisionKind);
          this.subdivisionValueCountyId = county.id;
          this.subdivisionLookupState.set(null);
        } else {
          this.clearSubdivision();
          this.subdivisionLookupState.set(result?.multiple_matches ? 'ambiguous' : 'none');
        }
        this.tryRecommendCourt(county);
      },
      error: () => {
        if (this.form.get('containing_subdivision_source')?.value === SubdivisionSource.MANUAL) {
          return;
        }
        this.clearSubdivision();
        this.subdivisionLookupState.set('none');
        this.tryRecommendCourt(county);
      }
    });
    return true;
  }
}
